// import { add, format } from 'date-fns';
import { add, format } from 'date-fns';
import produce from 'immer';
import { AnyAction } from 'redux';
import { formattedAmount } from './../utils/index';
import {
  IN_LEASE_PRICING_ACTIONS,
  NEW_LEASE_ACTIONS,
} from '../actions/leasePricing';
import { ALL } from '../constants';
import { SFRDataItem, SFRLeasePriceState } from '../types/sfrLeasePricing';
import { generateLeaseKey } from './../actions/schema';

const defaultOption = { id: ALL, name: ALL };
const initialState: SFRLeasePriceState = {
  selectedOfferCycle: undefined,
  isLoading: false,
  offers: {},
  beds: [defaultOption],
  submarkets: [defaultOption],
  markets: [defaultOption],
  periodSelect: [
    { id: '3', name: 'last 12 months' },
    { id: '2', name: 'last 6 months' },
    { id: '1', name: 'last 4 months' },
    { id: '0', name: 'last 3 months' },
  ],
  sortBy: [
    { id: 'Beds-DESC', name: 'Bedrooms (high to low)' },
    { id: 'Beds-ASC', name: 'Bedrooms (low to high)' },
    { id: 'Sqft-DESC', name: 'Sq footage (high to low)' },
    { id: 'Sqft-ASC', name: 'Sq footage (low to high)' },
    // { id: '0', name: 'Best offer (high to low)' },
    // { id: '1', name: 'Best offer (low to high)' },
  ],
  filters: {
    sortBy: { id: 'Beds-DESC', name: 'Bedrooms (high to low)' },
    bed: defaultOption,
    submarket: defaultOption,
    market: defaultOption,
    period: { id: '3', name: 'last 12 months' },
    offerStatus: 'InReview',
  },
  occupancySummary: [],
  limit: 10,
  offset: 0,
  pageCount: 0,
  original: {},
  priceMatrices: new Map(),
  priceMatricesLoading: new Map(),
};

const sfrLeasePricingReducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case NEW_LEASE_ACTIONS.SFR_NEW_LEASE_OFFERS:
      return produce(state, (draft) => {
        draft.offers[action.payload.offerCycle] = action.payload.unitTypeData;
        draft.pageCount = Math.ceil(Number(action.payload.count) / draft.limit);
        draft.selectedOfferCycle = action.payload.offerCycle;
      });
    case NEW_LEASE_ACTIONS.SFR_PROPERTIES: {
      return produce(state, (draft) => {
        draft.beds = [defaultOption].concat(action.payload.beds);
        draft.submarkets = [defaultOption].concat(action.payload.submarkets);
        draft.markets = [defaultOption].concat(action.payload.markets);
        draft.original = action.payload;
      });
    }
    case IN_LEASE_PRICING_ACTIONS.APPROVE: {
      return produce(state, (draft) => {
        const { property_code, unit_Type } = action.payload;

        if (
          !draft.selectedOfferCycle ||
          (state.selectedOfferCycle && !draft.offers[state.selectedOfferCycle])
        )
          return;

        if (draft.filters.offerStatus !== undefined) {
          const dataItem: any = draft.offers[draft.selectedOfferCycle].filter(
            (elem: SFRDataItem) =>
              elem.Property_Code !== property_code &&
              elem.UnitType_Code !== unit_Type
          );
          draft.offers[draft.selectedOfferCycle] = dataItem;
        } else {
          const dataItem: SFRDataItem = draft.offers[
            draft.selectedOfferCycle
          ].find(
            (elem: SFRDataItem) =>
              elem.Property_Code === property_code &&
              elem.UnitType_Code === unit_Type
          );

          if (dataItem) {
            dataItem.New_Lease_Price?.forEach((elem) => {
              elem.Is_Approved = !elem.Is_Approved;
              elem.Is_Excluded = false;
            });
          }
        }
      });
    }
    case IN_LEASE_PRICING_ACTIONS.EXCLUDE: {
      return produce(state, (draft) => {
        const { property_code, unit_Type } = action.payload;
        if (
          !draft.selectedOfferCycle ||
          (state.selectedOfferCycle && !draft.offers[state.selectedOfferCycle])
        )
          return;
        if (draft.filters.offerStatus !== undefined) {
          const dataItem: any = draft.offers[draft.selectedOfferCycle].filter(
            (elem: SFRDataItem) =>
              elem.Property_Code !== property_code &&
              elem.UnitType_Code !== unit_Type
          );
          draft.offers[draft.selectedOfferCycle] = dataItem;
        } else {
          const dataItem: SFRDataItem = draft.offers[
            state.selectedOfferCycle
          ].find(
            (elem: SFRDataItem) =>
              elem.Property_Code === property_code &&
              elem.UnitType_Code === unit_Type
          );
          if (dataItem) {
            dataItem.New_Lease_Price?.forEach((elem) => {
              elem.Is_Approved = false;
              elem.Is_Excluded = !elem.Is_Excluded;
            });
          }
        }
      });
    }
    case 'NEW_LEASE_UPDATE_FLAG': {
      return produce(state, (draft) => {
        const flagItem = action.payload;
        if (state.selectedOfferCycle) {
          const dataItem: SFRDataItem = draft.offers[
            state.selectedOfferCycle
          ].find(
            (elem: SFRDataItem) =>
              elem.Property_Code === flagItem.info.property_code &&
              elem.UnitType_Code === flagItem.info.unit_type
          );
          if (dataItem) {
            dataItem.Is_Flagged = !flagItem.resolved;
          }
        }
      });
    }

    case NEW_LEASE_ACTIONS.UPDATE_BED: {
      return produce(state, (draft) => {
        const bedId = action.payload;

        const bedObj = state.beds.find(
          (elem) => elem.id.toString() === bedId.toString()
        );
        draft.offset = 0;
        if (bedObj) {
          draft.pageCount = Math.ceil(state.submarkets.length / draft.limit);
          draft.filters = {
            ...draft.filters,
            bed: bedObj,
          };
        }
      });
    }
    case NEW_LEASE_ACTIONS.UPDATE_MARKET: {
      return produce(state, (draft) => {
        const market = action.payload;
        let filteredProperties = state.original.properties;

        if (market !== ALL)
          filteredProperties = filteredProperties.filter(
            (elem: any) => elem.Market === market
          );
        const bedSet = new Set();
        const submarkets: any[] = [];
        const beds: any[] = [];

        filteredProperties.forEach((elem: any) => {
          if (!bedSet.has(elem.Beds)) {
            bedSet.add(elem.Beds);
            beds.push({
              id: elem.Beds,
              name: elem.Beds,
            });
          }

          submarkets.push({
            id: elem.Property_Code,
            name: elem.SubMarket,
          });
        });

        draft.submarkets = [defaultOption].concat(submarkets);
        draft.beds = [defaultOption].concat(beds);
        draft.offset = 0;
        draft.filters = {
          ...draft.filters,
          market: { id: market, name: market },
          submarket:
            submarkets.find((elem) => elem.id === state.filters.submarket.id) ||
            defaultOption,
          bed:
            beds.find((elem) => elem.id === state.filters.bed.id) ||
            defaultOption,
        };
      });
    }
    case NEW_LEASE_ACTIONS.UPDATE_PERIOD: {
      return produce(state, (draft) => {
        const periodId = action.payload;
        const period = state.periodSelect.find(
          (elem) => elem.id.toString() === periodId.toString()
        );
        if (period) draft.filters = { ...draft.filters, period };
      });
    }
    case NEW_LEASE_ACTIONS.UPDATE_OFFER_STATUS: {
      return produce(state, (draft) => {
        draft.filters.offerStatus = action.payload;
        draft.offset = 0;
      });
    }
    case NEW_LEASE_ACTIONS.UPDATE_UNITAVAILABILITYSTATUS: {
      return produce(state, (draft) => {
        draft.filters.unitAvailabilityStatus = action.payload;
        draft.offset = 0;
      });
    }
    case NEW_LEASE_ACTIONS.UPDATE_SORTBY: {
      return produce(state, (draft) => {
        const sortById = action.payload;
        const sortByObj = state.sortBy.find(
          (elem) => elem.id.toString() === sortById.toString()
        );
        if (sortByObj) draft.filters = { ...draft.filters, sortBy: sortByObj };
      });
    }
    case NEW_LEASE_ACTIONS.UPDATE_SUBMARKET: {
      return produce(state, (draft) => {
        const submarketId = action.payload;
        const submarket = state.submarkets.find(
          (elem) => elem.id.toString() === submarketId.toString()
        );

        if (submarket) {
          let filteredProperties = state.original.properties;
          if (state.filters.market.id !== ALL)
            filteredProperties = filteredProperties.filter(
              (elem: any) => elem.Market === state.filters.market.id
            );
          if (submarketId !== ALL) {
            filteredProperties = filteredProperties.filter(
              (elem: any) => elem.Property_Code === submarketId
            );
          }
          const bedSet = new Set();
          const beds: any[] = [];

          filteredProperties.forEach((elem: any) => {
            if (!bedSet.has(elem.Beds)) {
              bedSet.add(elem.Beds);
              beds.push({
                id: elem.Beds,
                name: elem.Beds,
              });
            }
          });
          draft.beds = [defaultOption].concat(beds);
          draft.filters = {
            ...draft.filters,

            submarket: submarket,
            bed:
              beds.find((elem) => elem.id === state.filters.bed.id) ||
              defaultOption,
          };
          draft.offset = 0;
        }
      });
    }
    case IN_LEASE_PRICING_ACTIONS.ADJUST_PRICE: {
      return produce(state, (draft) => {
        const { property_code, unit_Type, term, adjustedPrice } =
          action.payload;

        if (
          !draft.selectedOfferCycle ||
          (draft.selectedOfferCycle && !draft.offers[draft.selectedOfferCycle])
        )
          return;

        draft.offers[draft.selectedOfferCycle] = state.offers[
          draft.selectedOfferCycle
        ].map((item: SFRDataItem) => {
          let newItem = { ...item };

          if (
            newItem.New_Lease_Price &&
            newItem.Property_Code === property_code
          ) {
            newItem.New_Lease_Price = newItem.New_Lease_Price.map(
              (pricingOffer) => {
                let newPricingOffer = { ...pricingOffer };
                if (pricingOffer.Term === term) {
                  newPricingOffer.Price = adjustedPrice;
                }
                return newPricingOffer;
              }
            );
          }

          return newItem;
        });

        // UPDATE VALUES IN MATRICES /////////////////////////////////////

        let matricesItemToUpdate: any = {
          ...draft.priceMatrices.get(
            generateLeaseKey(property_code, unit_Type)
          ),
        };

        /*if (matricesItemToUpdate) {
         */

        if (Object.keys(matricesItemToUpdate).length > 0) {
          matricesItemToUpdate.terms = matricesItemToUpdate.terms.map(
            (item, i) => {
              let newItem = item.split(' ');
              newItem = newItem[0];
              if (term === Number(newItem)) {
                matricesItemToUpdate.prices[0][i] =
                  formattedAmount(adjustedPrice);
                let oldBestPrice = Number(
                  matricesItemToUpdate.bestPrices[0]
                    .replace('$', '')
                    .replace(',', '')
                );
                if (oldBestPrice >= Number(adjustedPrice)) {
                  matricesItemToUpdate.bestPrices[0] =
                    formattedAmount(adjustedPrice);
                } else {
                  let pricesForEearliestTerm: Array<number> = [];
                  matricesItemToUpdate.prices[0].forEach(
                    (item: string | number) => {
                      let itemPrice = Number(
                        String(item).replace('$', '').replace(',', '')
                      );
                      pricesForEearliestTerm.push(itemPrice);
                    }
                  );
                  matricesItemToUpdate.bestPrices[0] = Math.min(
                    ...pricesForEearliestTerm
                  );
                  matricesItemToUpdate.bestPrices[0] = formattedAmount(
                    matricesItemToUpdate.bestPrices[0]
                  );
                }
              }
              return item;
            }
          );
          draft.priceMatrices.set(property_code, matricesItemToUpdate);
        }
      });
    }
    case IN_LEASE_PRICING_ACTIONS.IS_LOADING: {
      return { ...state, isLoading: action.payload };
    }
    case NEW_LEASE_ACTIONS.OCCUPANCY_REPORTS: {
      return produce(state, (draft) => {
        let occupancySummary = [];
        for (let i = 0; i < 12; i++) {
          const date = add(new Date(), { months: 0 - i });
          const prevDateKey = format(add(date, { years: -1 }), 'MMM-yy');
          const dateKey = format(date, 'MMM-yy');
          occupancySummary.push({
            month: format(date, 'MMM'),
            year: action.payload.data.find((elem: any) => elem.name === dateKey)
              .value,
            lastYear: action.payload.data.find(
              (elem: any) => elem.name === prevDateKey
            ).value,
          });
        }
        draft.occupancySummary = occupancySummary.reverse();
      });
    }
    case IN_LEASE_PRICING_ACTIONS.SET_PRICE_MATRICES: {
      return produce(state, (df) => {
        const { priceMatricesData, UnitTypeKey } = action.payload;
        df.priceMatrices.set(UnitTypeKey, priceMatricesData);
      });
    }
    case IN_LEASE_PRICING_ACTIONS.PRICE_MATRICES_IS_LOADING: {
      return produce(state, (df) => {
        const { isLoading, UnitTypeKey } = action.payload;
        df.priceMatricesLoading.set(UnitTypeKey, isLoading);
      });
    }
    case NEW_LEASE_ACTIONS.UPDATE_OFFSET: {
      return produce(state, (draft) => {
        draft.offset = action.payload;
      });
    }
    default:
      return state;
  }
};

export default sfrLeasePricingReducer;
